import { useState } from 'react';
import type { Server } from '../types';
import { mockVirtualMachines, mockCloudPCs } from '../mockData';

interface ServerDetailModalProps {
  server: Server;
  onClose: () => void;
  onToggleStatus: (id: string) => void;
}

export default function ServerDetailModal({ server, onClose, onToggleStatus }: ServerDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'vms' | 'cloudpcs'>('info');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">服务器详情</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors whitespace-nowrap">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="border-b border-gray-200">
          <div className="flex gap-8 px-6">
            <button
              onClick={() => setActiveTab('info')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'info' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              基本信息
            </button>
            <button
              onClick={() => setActiveTab('vms')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'vms' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              承载虚拟机列表
            </button>
            <button
              onClick={() => setActiveTab('cloudpcs')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'cloudpcs' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              运行云电脑列表
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">所属机房</label>
                  <div className="text-sm text-gray-900">{server.datacenter}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">主机编号</label>
                  <div className="text-sm text-gray-900">{server.serverNumber}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">主机别名</label>
                  <div className="text-sm text-gray-900">{server.serverAlias}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    server.status === 'normal' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {server.status === 'normal' ? '正常' : '停用'}
                  </span>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">资源信息</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <i className="ri-cpu-line text-blue-600"></i>
                      </div>
                      <span className="text-sm font-medium text-gray-900">CPU</span>
                    </div>
                    <div className="text-xs text-gray-600 mb-1">可用/总数</div>
                    <div className="text-lg font-semibold text-gray-900 mb-2">{server.cpu.available} / {server.cpu.total} 核</div>
                    <div className="text-xs text-gray-500">型号: {server.cpu.model}</div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <i className="ri-database-2-line text-purple-600"></i>
                      </div>
                      <span className="text-sm font-medium text-gray-900">内存</span>
                    </div>
                    <div className="text-xs text-gray-600 mb-1">可用/总数</div>
                    <div className="text-lg font-semibold text-gray-900">{server.memory.available} / {server.memory.total} GB</div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <i className="ri-hard-drive-2-line text-green-600"></i>
                      </div>
                      <span className="text-sm font-medium text-gray-900">硬盘</span>
                    </div>
                    <div className="text-xs text-gray-600 mb-1">可用/总数</div>
                    <div className="text-lg font-semibold text-gray-900 mb-2">{server.disk.available} / {server.disk.total} GB</div>
                    <div className="text-xs text-gray-500">类型: {server.disk.type}</div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <i className="ri-gamepad-line text-orange-600"></i>
                      </div>
                      <span className="text-sm font-medium text-gray-900">GPU</span>
                    </div>
                    <div className="text-xs text-gray-600 mb-1">可用/总数</div>
                    <div className="text-lg font-semibold text-gray-900 mb-2">{server.gpu.available} / {server.gpu.total} 张</div>
                    <div className="text-xs text-gray-500">型号: {server.gpu.model}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'vms' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">虚拟机名称</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">虚拟机 ID</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">状态</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">CPU</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">内存</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">系统盘</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">GPU</th>
                  </tr>
                </thead>
                <tbody>
                  {mockVirtualMachines.map((vm) => (
                    <tr key={vm.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{vm.name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{vm.id}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          vm.status === 'normal' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {vm.status === 'normal' ? '正常' : '停用'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                        <div>{vm.cpu.cores} 核</div>
                        <div className="text-xs text-gray-500">{vm.cpu.model}</div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{vm.memory.capacity} GB</td>
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                        <div>{vm.systemDisk.capacity} GB</div>
                        <div className="text-xs text-gray-500">{vm.systemDisk.type}</div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                        {vm.gpu.count > 0 ? (
                          <>
                            <div>{vm.gpu.count} 张</div>
                            <div className="text-xs text-gray-500">{vm.gpu.model}</div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'cloudpcs' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">云电脑ID</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">云电脑名称</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">所属用户</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">配置详情</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">创建时间</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">运行时长</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 whitespace-nowrap">所属虚拟机ID</th>
                  </tr>
                </thead>
                <tbody>
                  {mockCloudPCs.map((pc) => (
                    <tr key={pc.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{pc.id}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{pc.name}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                        <div>{pc.user.username}</div>
                        <div className="text-xs text-gray-500">{pc.user.phone}</div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{pc.config}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{pc.createTime}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{pc.runningTime}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{pc.vmId || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => onToggleStatus(server.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              server.status === 'normal'
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            {server.status === 'normal' ? '下线' : '上线'}
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap">
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
